import {
  $Keys,
} from 'utility-types'

import {
  BigNumber,
} from 'ethers'

import {
  encodeTokenIdOldStyle,
} from './token_id'

import tenTokensRaw from './fixtures/ten_tokens'
const tenTokens = tenTokensRaw as Record<string, string>

export interface IProviderConfig {
  title: string,
  driver: string,
  port?: number,
  url?: string,
  developmentOnly?: boolean,
}

export interface INetworkConfig {
  title: string,
  chainId: number,
  providers: IProviderName[],
  blockNetwork?: INetworkName,
  developmentOnly?: boolean,
}

export interface IContractConfig {
  name: string,
  title: string,
}

export interface ISingleSeriesTokenConfig {
  name: string,
  symbol: string,
  supply: BigNumber,
  uri: string,
}

/*

  general

*/
export const IS_PRODUCTION = process.env.NODE_ENV == 'production'

export const ERROR_TYPES = {
  None: 0,
  RevertWithMessage: 1,
  RevertWithoutMessage: 2,
  Panic: 3, 
}

/*

  network & providers

*/
export const PROVIDERS: Record<string, IProviderConfig> = {
  infura: {
    title: 'Infura (infura.io)',
    driver: 'infura',
  },
  etherscan: {
    title: 'Etherscan (etherscan.io)',
    driver: 'etherscan',
  },
  alchemy: {
    title: 'Alchemy (alchemy.com)',
    driver: 'alchemy',
  },
  cloudflare: {
    title: 'Cloudflare (cloudflare.com)',
    driver: 'cloudflare',
  },
  // A public mainnet RPC that needs no key and allows browser (CORS) requests. The site is static
  // since 2026-10-19, so no backend holds an Alchemy key for it.
  publicnode: {
    title: 'PublicNode (publicnode.com)',
    driver: 'jsonrpc_url',
    url: 'https://ethereum-rpc.publicnode.com',
  },
  localhost: {
    title: 'Local Hardhat JSONRPC',
    driver: 'jsonrpc',
    port: 8545,
    developmentOnly: true,
  },
  ganache: {
    title: 'Local Ganache JSONRPC',
    driver: 'jsonrpc',
    port: 8546,
    developmentOnly: true,
  }
}

export type IProviderName = $Keys<typeof PROVIDERS>
export const PROVIDER_NAME_DEFAULT: IProviderName = IS_PRODUCTION ? 'cloudflare' : 'localhost'
export const PROVIDER_NAMES: IProviderName[] = Object
  .keys(PROVIDERS)
  .filter(name => {
    if(!IS_PRODUCTION) return true
    return PROVIDERS[name].developmentOnly ? false : true
  })



export const NETWORKS: Record<string, INetworkConfig> = {
  localhost: {
    title: "Hardhat (dev)",
    chainId: 31337,
    providers: [
      'localhost'
    ],
    blockNetwork: 'mainnet',
    developmentOnly: true,
  },
  ganache: {
    title: "Ganache (dev)",
    chainId: 31337,
    providers: [
      'ganache'
    ],
    blockNetwork: 'mainnet',
    developmentOnly: true,
  },
  rinkeby: {
    title: "Rinkeby (test)",
    chainId: 4,
    providers: [
      'infura',
      'etherscan',
      'alchemy',
    ],
  },
  mainnet: {
    title: "Mainnet (live)",
    chainId: 1,
    providers: [
      'publicnode',
      'alchemy',
      //'cloudflare',
      // 'infura',
      // 'etherscan',
    ],
  },
}

export type INetworkName = $Keys<typeof NETWORKS>
export const NETWORK_NAME_DEFAULT: INetworkName = IS_PRODUCTION ? 'mainnet' : 'mainnet'
export const NETWORK_NAMES: IProviderName[] = Object
  .keys(NETWORKS)
  .filter(name => {
    if(!IS_PRODUCTION) return true
    return NETWORKS[name].developmentOnly ? false : true
  })

export const CONTRACTS: Record<string, IContractConfig> = {
  TokenStorage: {
    name: 'TokenStorage',
    title: 'The storage contract for NFT media'
  }
}

export const SINGLE_SERIES_TOKENS: Record<string, ISingleSeriesTokenConfig> = {
  systemTest: {
    name: 'System Test',
    symbol: 'SYS',
    supply: BigNumber.from('3'),
    uri: 'ar://-CkUFnvXEHLqPi6loNVa_TGxF8viYHhSleG4ZVcG4DM/',
  },
  systemTest2: {
    name: 'System Test2',
    symbol: 'SYS2',
    supply: BigNumber.from('3'),
    uri: 'ar://a7K-91dQi6gxRq78k9mXQ5lyVSRqj94Z2Fy0hpdP_HE/',
  },
}

export const GODPIXEL_TOKEN = {
  name: 'Subject to Change - Hyperreality and the Godpixel',
  symbol: 'GODPIXEL',
  commission: BigNumber.from('500'),
}

export const BOWIE_TOKEN = {
  id: BigNumber.from('1'),
  name: 'Opus 1',
  symbol: 'ETERNAL',
  commission: BigNumber.from('500'),
}

export type IContractName = $Keys<typeof CONTRACTS>

/*

  tokens

*/

export const DROP_TYPES = {
  None: BigNumber.from(0),
  BuyNow: BigNumber.from(1),
  Auction: BigNumber.from(2),
  Offline: BigNumber.from(3),
}

export const TOKEN_NAME = 'panwww collection'
export const TOKEN_SYMBOL = 'PANWWW'
export const TOKEN_URI = 'https://token-cdn-domain.com/'

export const WEI_IN_GWEI = BigNumber.from('1000000000')
export const GWEI_IN_ETH = BigNumber.from('1000000000')

// how many bytes in one storage slot
export const ETH_STORAGE_SLOT_BYTES = 32

// how many bits in a byte
export const BYTE_SIZE = 8

// the max limit on gas for each block
// no transaction can exceed this and it's worth using < 50% of it
// TODO: find out the best % here - the more % we can use the less transactions
// we need (so cheaper to loop)
export const ETH_BLOCK_GAS_LIMIT = 15000000
export const RINKEBY_BLOCK_GAS_LIMIT = 10000000
// how much gas does a create file tx cost
export const CREATE_FILE_GAS_LIMIT = 130000
// how much gas does a finalize cost
export const FINALIZE_GAS_LIMIT = 60000
// this is the solidity gas cost for a single storage slot of 32 bytes
export const IMAGE_GAS_PER_SLOT = 20000
// how much gas we want to spend per transaction - this is the gasLimit in other words
export const IMAGE_GAS_STORAGE_PER_TRANSACTION = 2000000
export const IMAGE_GAS_MISC_PER_TRANSACTION = IMAGE_GAS_STORAGE_PER_TRANSACTION * 0.2
// how many storage slots do we put per tx
export const IMAGE_BLOCKS_PER_TRANSACTION = IMAGE_GAS_STORAGE_PER_TRANSACTION / IMAGE_GAS_PER_SLOT
// the total gas limit based on above (we have leway in the )
export const IMAGE_GAS_LIMIT = IMAGE_GAS_STORAGE_PER_TRANSACTION + IMAGE_GAS_MISC_PER_TRANSACTION

// the first series!
export const GAVIN_ID = BigNumber.from('1')
export const BOWIE_SHH_SERIES_ID = BigNumber.from('1')
export const BOWIE_SHH_GROUP_SUPPLY = BigNumber.from('1')
export const BOWIE_SHH_TOKEN_SUPPLY = BigNumber.from('1')

// the token id in the storage contract (the global namespace id)
// this is just storage not ERC721
export const BOWIE_SHH_TOKEN_ID_STORAGE = encodeTokenIdOldStyle(GAVIN_ID, BOWIE_SHH_SERIES_ID, BigNumber.from('1'), BigNumber.from('1'))

// the token id in the panevision ERC721 contract
// bowie is #1
export const BOWIE_SHH_TOKEN_ID_PANEVISION = BigNumber.from('1')

export const BOWIE_SHH_TOKEN_ID = BOWIE_SHH_TOKEN_ID_STORAGE

export const BOWIE_SSH_IMAGE_SIZE_MIN = 200
export const BOWIE_SSH_IMAGE_SIZE_MAX = 720
export const BOWIE_WIDE_PAGE_SIZE_MIN = 400

export const BOWIE_SSH_IMAGE_FILENAME = 'Bowie DB03©Gavin Evans.jpg'
export const BOWIE_SSH_TEXT_FILENAME = 'Bowie DB03©Gavin Evans.txt'
export const BOWIE_SSH_META_FILENAME = 'Bowie DB03 Metadata.txt'

export const BOWIE_SSH_IMAGE_BYTES = 77532
export const BOWIE_SSH_IMAGE_SIZE = 1000

// TODO: THIS MUST BE CHANGED BEFORE PROD
export const BOWIE_SSH_METADATA_BYTES_TEST = 1076

// if we are deploying an opensea contract then we need these for the constructor
// https://github.com/ProjectOpenSea/opensea-creatures/blob/master/migrations/2_deploy_contracts.js#L28
export const OPENSEA_PROXY_REGISTRY_ADDRESSES = {
  rinkeby: '0xf57b2c51ded3a29e6891aba85459d600256cf317',
  mainnet: '0xa5409ec958c83c3f309868babaca7c86dcb077c1'
}

export const STORAGE_URL = `https://storage.googleapis.com/pannft-files`

export const getStorageUrl = (path: string) => `${STORAGE_URL}/${path}`

export const STORAGE_URL_GODPIXEL = `${STORAGE_URL}/projects/godpixel`

export type IGravenPartName = 'Left' | 'Centre' | 'Right'
export const STORAGE_URL_GRAVEN = `${STORAGE_URL}/projects/graven`
export const GRAVEN_IMAGE_NAMES = [
  'Ai-Weiwei',
  'Björk',
  'Bowie-A',
  'Bowie-B',
  'Dusty-Springfield',
  'Ewan-McGregor',
  'Iggy-Pop',
  'Juliette-Binoche',
  'Nick-Cave',
  'ODB',
  'Ozzy-Osbourne',
  'Peter-Fonda',
]

export const getGravenImagePath = (name: string, part: IGravenPartName, size = '300') => `${STORAGE_URL_GRAVEN}/${size}/${name}-${part}.png`

export type ITens_Image_Size = 'large' | 'medium' | 'small'

export const CONTRACT_ADDRESS_TENS = '0x495f947276749ce646f68ac8c248420045cb7b5e'
export const STORAGE_URL_TENS = `${STORAGE_URL}/projects/101010/nfts`

export const getTensFilename = (index: number) => {
  let ret = '' + index
  while(ret.length < 3) {
    ret = '0' + ret
  }
  return `101010-${ret}.jpg`
}

export const getTensTokenId = (index: number) => tenTokens[getTensFilename(index)]
export const getTensOpenseaUrl = (index: number) => `https://opensea.io/assets/${CONTRACT_ADDRESS_TENS}/${getTensTokenId(index)}`
export const getTensImagePath = (index: number, size: ITens_Image_Size = 'medium') => `${STORAGE_URL_TENS}/${size}/${getTensFilename(index)}`

export const TENS_COLLECTION_NAME = '101010-gavinevans'
export const TENS_TOTAL_IMAGES = 1000
export const TENS_TOTAL_SETS = 10
export const TENS_SET_SIZE = TENS_TOTAL_IMAGES / TENS_TOTAL_SETS
export const TENS_SIZES = {
  large: [4961, 3508],
  medium: [1024, 724],
  small: [200, 141]
}
export const TENS_RATIO = 0.7071155009
export const TENS_MAP_RANGE = {
  gold: [1, 10],
  yellow: [11, 50],
  yellow_red: [51, 90],
  orange: [91, 145],
  megenta_orange: [146, 195],
  purple_magenta: [196, 250],
  red: [251, 360],
  blue: [361, 420],
  green: [421, 470],
  black: [471, 1000],
}

// add this number per set and everything will line up
export const TENS_SET_COUNT = {
  gold: 10,
  yellow: 40,
  yellow_red: 40,
  orange: 55,
  megenta_orange: 50,
  purple_magenta: 55,
  red: 110,
  blue: 60,
  green: 50,  
  black: 530
}

export const TENS_COLOR_TITLES: Record<string, string> = {
  gold: 'Gold',
  yellow: 'Yellow',
  yellow_red: 'Yellow & Red',
  orange: 'Orange',
  megenta_orange: 'Magenta & Orange',
  purple_magenta: 'Purple & Magenta',
  red: 'Red',
  blue: 'Blue',
  green: 'Green',  
  black: 'Black'
}

export const TENS_COLORS_HEX: Record<string, string> = {
  gold: '#d7ac46',
  yellow: '#f3e500',
  yellow_red: '#f24795',
  orange: '#f04f07',
  megenta_orange: '#d63000',
  purple_magenta: '#774385',
  red: '#e83934',
  blue: '#0059c9',
  green: '#46b535',  
  black: '#2e2e2e'
}

export type ITens_Color = $Keys<typeof TENS_MAP_RANGE>
export const TENS_COLORS: ITens_Color[] = Object.keys(TENS_MAP_RANGE) as ITens_Color[]

export const tensGetColor = (index: number): string => {
  const color = TENS_COLORS.find(colorName => {
    const [start,end] = TENS_MAP_RANGE[colorName]
    return index >= start && index <= end
  })
  return color || ''
}

export const tensGetColorTitle = (index: number): string => {
  const color = tensGetColor(index) || 'black'
  return TENS_COLOR_TITLES[color]
}

export const tensGetColors = (index: number): string[] => tensGetColor(index).split('_')

export const getSetColorCount = (set: number, color: ITens_Color): number => {
  if(color == 'orange') {
    return set % 2 == 0 ? 5 : 6
  }
  else if(color == 'purple_magenta') {
    return set % 2 == 0 ? 6 : 5
  }
  else {
    return TENS_SET_COUNT[color] / TENS_TOTAL_SETS
  }
}

// split all images into even sets of 100
// across all colours
export const getSets = () => {
  let usedColors: Partial<Record<ITens_Color, number>> = {}
  let sets: number[][] = []
  for(let setIndex=0; setIndex<TENS_TOTAL_SETS; setIndex++) {
    let set: number[] = []
    TENS_COLORS.forEach(color => {
      const colorCount = getSetColorCount(setIndex, color)
      const colorUsed = usedColors[color] || 0
      const start = TENS_MAP_RANGE[color][0] + colorUsed
      for(let i=start; i<start+colorCount; i++) {
        set.push(i)
      }
      usedColors[color] = colorUsed + colorCount
    })
    sets.push(set)
  }
  return sets
}

export const checkSets = () => {
  const seen: Record<number, boolean> = {}
  const sets = getSets()

  sets.forEach(set => {
    set.forEach(image => {
      if(seen[image]) throw new Error(`Duplicate image ${image}`)
      seen[image] = true
    })
  })

  const lengths = sets.map(set => set.length)
  console.log(lengths)
}